import React, { useEffect, useRef, useState } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { TranslationProvider } from './providers/TranslationProvider';
import { AuthProvider } from './contexts/AuthContext';
import { UIProvider } from './contexts/UIContext';
import ScreenManager from './components/core/ScreenManager';
import ThemeWrapper from './components/ThemeWrapper';
import PublicMicrositeApp, { shouldRenderPublicMicrosite } from './publicMicrosite/PublicMicrositeApp';
import './styles/global.css';
import './styles/global-styles.css';
import { APP_SETTINGS } from './utils/constants';
import { isUserInactive, updateLastActive } from './utils/storage';

const InternalApp = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialTheme, setInitialTheme] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const isDarkMode = savedTheme ? savedTheme === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
    setInitialTheme(isDarkMode);

    document.body.classList.add(isDarkMode ? 'dark-theme' : 'light-theme');

    const fontSizeScale = localStorage.getItem('fontSizeScale') || '1';
    document.documentElement.style.setProperty('--font-size-scale', fontSizeScale);

    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
      );
    }

    const statusBarMeta = document.querySelector('meta[name="theme-color"]') || document.createElement('meta');
    statusBarMeta.name = 'theme-color';
    statusBarMeta.content = isDarkMode ? '#020617' : '#ffffff';
    if (!statusBarMeta.parentNode) {
      document.head.appendChild(statusBarMeta);
    }

    updateLastActive();

    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      document.documentElement.classList.add('standalone-mode');
    }

    const checkInactivity = () => {
      if (isUserInactive(APP_SETTINGS.INACTIVITY_TIMEOUT)) {
        return null;
      }
      return null;
    };

    const handleUserActivity = () => updateLastActive();
    const inactivityInterval = setInterval(checkInactivity, 60000);

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);

    setIsInitialized(true);

    return () => {
      clearInterval(inactivityInterval);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
    };
  }, []);

  useEffect(() => {
    const handleTouchMove = (event) => {
      if (document.body.classList.contains('pull-gesture-active')) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  useEffect(() => {
    const handleThemeChange = (event) => {
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.content = event.detail.isDarkMode ? '#020617' : '#ffffff';
      }
    };

    window.addEventListener('themeChanged', handleThemeChange);
    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  useEffect(() => {
    const handleFontSizeChange = (event) => {
      document.documentElement.style.setProperty('--font-size-scale', event.detail.fontSizeScale);
    };

    window.addEventListener('fontSizeChanged', handleFontSizeChange);
    return () => {
      window.removeEventListener('fontSizeChanged', handleFontSizeChange);
    };
  }, []);

  if (!isInitialized || initialTheme === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="w-24 h-24 bg-primary-600 rounded-full flex items-center justify-center">
          <div className="text-3xl font-bold text-white flex items-center">
            <span>id</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={initialTheme ? 'dark-theme' : 'light-theme'}>
      <LanguageProvider>
        <TranslationProvider>
          <AuthProvider>
            <UIProvider initialTheme={initialTheme}>
              <ThemeWrapper>
                <ScreenManager />
              </ThemeWrapper>
            </UIProvider>
          </AuthProvider>
        </TranslationProvider>
      </LanguageProvider>
    </div>
  );
};

const App = () => {
  if (typeof window !== 'undefined' && shouldRenderPublicMicrosite(window.location)) {
    return <PublicMicrositeApp />;
  }

  return <InternalApp />;
};

export default App;
